import React, { useState } from "react";
import { toast } from "sonner";
import { StickyNote, X } from "lucide-react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

const when = (iso) => new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", dateStyle: "medium", timeStyle: "short" });

export function RegistrationNotes({ reg, onChanged }) {
  const { user, can } = useAuth();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const notes = reg.notes || [];
  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post(`/admin/registrations/${reg.id}/notes`, { text }); setText(""); onChanged?.(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (n) => {
    try { await api.delete(`/admin/registrations/${reg.id}/notes/${n.id}`); onChanged?.(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  return (
    <div className="w-full" data-testid={`reg-notes-${reg.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center gap-1 text-xs font-semibold text-palm underline" data-testid={`reg-notes-toggle-${reg.id}`}>
        <StickyNote size={13} /> {open ? "Hide" : "Vetting notes"} ({notes.length})
      </button>
      {open && (
        <div className="mt-2 space-y-2 rounded-lg border border-gold/20 bg-parchment/60 p-3">
          {notes.length === 0 && <p className="text-xs text-muted-foreground" data-testid={`reg-notes-empty-${reg.id}`}>No vetting notes yet. Record what the applicant shared during vetting for the Executive Board.</p>}
          {notes.map((n) => (
            <div key={n.id} className="flex items-start justify-between gap-2 text-sm" data-testid={`reg-note-${n.id}`}>
              <div><p className="whitespace-pre-wrap">{n.text}</p><p className="text-xs text-muted-foreground">{n.by} · {when(n.at)}</p></div>
              {(can("admin_level") || n.by_id === user?.id) && <button type="button" onClick={() => remove(n)} className="text-muted-foreground hover:text-crimson" aria-label="Remove note" data-testid={`reg-note-delete-${n.id}`}><X size={14} /></button>}
            </div>
          ))}
          <div className="flex gap-2">
            <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Spoke with applicant on 6/12 — confirmed Akropong lineage, explained dues and good-standing rules." data-testid={`reg-note-input-${reg.id}`} />
            <Button size="sm" className="bg-palm self-end" disabled={busy || !text.trim()} onClick={add} data-testid={`reg-note-add-${reg.id}`}>Add note</Button>
          </div>
        </div>
      )}
    </div>
  );
}
